import { PredefinedProfession } from '/@/modules/professional/enums/PredefinedProfession';
import { UserCollection } from '/@/modules/user/models/UserCollection';
import {
  estateAssignableUsers,
  estateAdministrators,
  professionalUsers
} from '@/modules/user/store/user.state';
import { AuthUser } from '@/modules/auth/models/AuthUser';
import { User } from '@/modules/user/models/User';
import { EstateAccount } from '@/modules/estateaccount/models/EstateAccount';
import { Profession } from '/@/modules/professional/models/Profession';
import { TeamUser } from '@/modules/team/models/TeamUser';

/**
 * @deprecated
 */
export function getAllEstateAssignableUsers(): User[] {
  return estateAssignableUsers.value.get() as User[];
}

/**
 * @deprecated
 */
export function getEstateAssignableUsers(authUser: AuthUser): UserCollection | undefined {
  if (!authUser || !estateAssignableUsers.value.hasItems) {
    return undefined;
  }

  const users: UserCollection = estateAssignableUsers.value;
  const self: User = estateAssignableUsers.value.findByKey(authUser.id) as User;

  if (authUser.isStaffAdminAccountant && authUser.permission!.canAssignUsers) {
    return getAccountantConsultants(authUser, self);
  }

  if (authUser.permission!.canAssignOnlySelfToEstate) {
    return new UserCollection([self]);
  }

  if (!authUser.permission!.canAssignEstateProfessional) {
    return new UserCollection(estateAssignableUsers.value.nonEstateProfessionalUsers);
  }

  return users;
}

/**
 * @deprecated
 */
export function getTaskAssignableUsers(authUser: AuthUser): UserCollection | undefined {
  if (!authUser || !estateAssignableUsers.value.hasItems) {
    return undefined;
  }

  const users: UserCollection = estateAssignableUsers.value;
  const self: User | undefined = estateAssignableUsers.value.findByKey(authUser.id);

  if (authUser.isJuniorEstateProfessional) {
    const teamUsers: User[] =
      users.items?.filter((user: User) =>
        user.teamUsers
          ?.map((teamUser: TeamUser) => teamUser.teamId)
          .includes(authUser.teamThatUserLeads?.id ?? 0)
      ) ?? [];

    const assignableUsers: User[] = users.estateProfessionalUsers ?? [];

    if (self) {
      assignableUsers.push(self);
    }

    return new UserCollection(authUser.isTeamLead ? teamUsers : assignableUsers);
  }

  return users;
}

/**
 * @deprecated
 */
export function getEstateAssignedUsers(estateAccounts: EstateAccount[]): User[] {
  const userIds: number[] = estateAccounts.map(
    (estateAccount: EstateAccount) => estateAccount.userId as number
  );
  return estateAssignableUsers.value
    .get()
    ?.filter((user: User) => userIds.includes(user.id as number)) as User[];
}

/**
 * @deprecated
 */
export function getEstateAdministrators(): User[] {
  return estateAdministrators.value;
}

/**
 * @deprecated
 */
export function getProfessionalUsers(): User[] {
  return professionalUsers.value;
}

/**
 * @deprecated
 */
export function getProfessionalUsersWithProfession(profession: Profession): User[] {
  return professionalUsers.value.filter(
    (user: User) => user.professional?.professionId === profession.id && !user.isDemo
  );
}

export function getAccountantConsultants(
  authUser: AuthUser,
  selfUser?: User
): UserCollection | undefined {
  if (!authUser || !estateAssignableUsers.value.hasItems) {
    return undefined;
  }

  let users: User[] | undefined = estateAssignableUsers.value.accountantConsultantUsers;

  if (selfUser) {
    users = users?.concat(selfUser);
  }

  return new UserCollection(users);
}

/**
 * @deprecated
 */
export function getAccountantAdminConsultants(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      user.professional?.professionId === PredefinedProfession.ACCOUNTANT.id &&
      !user.isDemo &&
      user.isAdmin &&
      user.isConsultant
  );
}

/**
 * @deprecated
 */
export function getNotaryConsultants(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      user.professional?.professionId === PredefinedProfession.NOTARY.id &&
      !user.isDemo &&
      user.isConsultant
  );
}

export function getNotaryAdminConsultants(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      user.professional?.professionId === PredefinedProfession.NOTARY.id &&
      !user.isDemo &&
      user.isAdmin &&
      user.isConsultant
  );
}

/**
 * @deprecated
 */
export function getLawyerConsultants(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      user.professional?.professionId === PredefinedProfession.LAWYER.id &&
      !user.isDemo &&
      user.isConsultant
  );
}

/**
 * @deprecated
 */
export function getLawyers(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      user.professional?.professionId === PredefinedProfession.LAWYER.id && !user.isDemo
  );
}

/**
 * @deprecated
 */
export function getLawyersAdmin(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      user.professional?.professionId === PredefinedProfession.LAWYER.id &&
      !user.isDemo &&
      user.isAdmin
  );
}

/**
 * @deprecated
 */
export function getLawyerAdminConsultants(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      user.professional?.professionId === PredefinedProfession.LAWYER.id &&
      !user.isDemo &&
      user.isAdmin &&
      user.isConsultant
  );
}

/**
 * @deprecated
 */
export function getEstateCoordinators(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      user.professional?.professionId === PredefinedProfession.ESTATE_COORDINATOR.id && !user.isDemo
  );
}

/**
 * @deprecated
 */
export function getEstateCoordinatorsAdmin(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      user.professional?.professionId === PredefinedProfession.ESTATE_COORDINATOR.id &&
      !user.isDemo &&
      user.isAdmin
  );
}

/**
 * @deprecated
 */
export function getFinancialAdvisorConsultants(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      [PredefinedProfession.FINANCIAL_ADVISOR.id, PredefinedProfession.GUARDIAN.id].includes(
        user.professional?.professionId as number
      ) &&
      !user.isDemo &&
      user.isConsultant
  );
}

/**
 * @deprecated
 */
export function getAssociateFinancialAdvisorConsultants(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      [PredefinedProfession.ASSOCIATE_FINANCIAL_ADVISOR.id].includes(
        user.professional?.professionId as number
      ) &&
      !user.isDemo &&
      user.isConsultant
  );
}

/**
 * @deprecated
 */
export function getFinancialAdvisorAssistantConsultants(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      [PredefinedProfession.FINANCIAL_ADVISOR_ASSISTANT.id].includes(
        user.professional?.professionId as number
      ) &&
      !user.isDemo &&
      user.isConsultant
  );
}

/**
 * @deprecated
 */
export function getTrustOfficerConsultantAdmins(): User[] {
  return professionalUsers.value.filter(
    (user: User) =>
      user.professional?.professionId === PredefinedProfession.TRUST_OFFICER.id &&
      !user.isDemo &&
      user.isAdmin &&
      user.isConsultant
  );
}
